import type { FAQPageContent } from '@/types/pageContent';

export const DEFAULT_FAQ: FAQPageContent = {
    categories: [
        {
            name: 'Booking & Reservations',
            faqs: [
                {
                    question: 'How do I make a reservation?',
                    answer: 'Simply browse our equipment catalog, select your dates and location, add items to your cart, and complete the checkout process. You\'ll receive a confirmation email with your reservation details immediately.',
                },
                {
                    question: 'How far in advance should I book?',
                    answer: 'We recommend booking at least 48-72 hours in advance, especially during peak travel seasons (summer, holidays). However, we often have same-day availability - just check online or contact us.',
                },
                {
                    question: 'Can I modify my reservation?',
                    answer: 'Yes! You can modify your reservation (change dates, add/remove items) up to 24 hours before your pickup time through your account or by contacting us. Changes are subject to availability.',
                },
                {
                    question: 'What if the equipment I want isn\'t available?',
                    answer: 'If specific items aren\'t available for your dates, we\'ll show you similar alternatives. You can also join our waitlist to be notified if availability opens up.',
                },
            ],
        },
        {
            name: 'Pricing & Payments',
            faqs: [
                {
                    question: 'How is the rental price calculated?',
                    answer: 'Rental prices are based on the duration of your rental. We offer daily rates with discounts for longer rentals (weekly rates are typically 20-30% lower than daily rates). All prices include applicable taxes.',
                },
                {
                    question: 'What is the security deposit?',
                    answer: 'A refundable security deposit is required for all rentals. The deposit amount varies by item value. It\'s held as a pre-authorization on your card (not charged) and released within 5-10 business days after return.',
                },
                {
                    question: 'What payment methods do you accept?',
                    answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe. We do not accept cash or bank transfers.',
                },
                {
                    question: 'Are there any hidden fees?',
                    answer: 'No hidden fees! The price shown at checkout includes all taxes and fees. You\'ll only be charged additional fees if equipment is returned late, damaged, or with missing parts.',
                },
            ],
        },
        {
            name: 'Pickup & Return',
            faqs: [
                {
                    question: 'Where do I pick up and return equipment?',
                    answer: 'Equipment is picked up and returned at our designated locations. The exact address and operating hours will be in your confirmation email. We also offer delivery to select accommodations for an additional fee.',
                },
                {
                    question: 'What do I need to bring for pickup?',
                    answer: 'Please bring your order confirmation (email or app) and a valid government-issued ID (passport or driver\'s license) that matches the name on the reservation.',
                },
                {
                    question: 'What are your operating hours?',
                    answer: 'Operating hours vary by location. Most locations are open 9AM-7PM daily during high season, with reduced hours in low season. Exact hours are shown when selecting your pickup time.',
                },
                {
                    question: 'What if I\'m late for pickup or return?',
                    answer: 'Please contact us if you\'ll be late. Late pickups may result in equipment no longer being available. Late returns may incur additional daily charges unless you\'ve arranged an extension.',
                },
            ],
        },
        {
            name: 'Equipment & Safety',
            faqs: [
                {
                    question: 'Is the equipment clean and safe?',
                    answer: 'Absolutely! All equipment is thoroughly cleaned, sanitized, and inspected between every rental. We follow strict hygiene protocols and only rent equipment that meets EU safety standards.',
                },
                {
                    question: 'What brands do you carry?',
                    answer: 'We stock premium brands known for quality and safety, including Babyzen, Bugaboo, Cybex, Maxi-Cosi, and more. We regularly update our inventory with the latest models.',
                },
                {
                    question: 'What if equipment is damaged or defective?',
                    answer: 'If you notice any issues with your equipment at pickup, let us know immediately. If a problem occurs during your rental, contact us and we\'ll arrange a replacement or refund.',
                },
                {
                    question: 'Do you provide instruction on how to use the equipment?',
                    answer: 'Yes! Our staff will demonstrate how to use each item at pickup. We also include printed instructions and quick-start guides with every rental.',
                },
            ],
        },
        {
            name: 'Cancellation & Refunds',
            faqs: [
                {
                    question: 'What is your cancellation policy?',
                    answer: 'Cancel more than 24 hours before pickup for a full refund. Cancellations within 24 hours may be subject to a 50% cancellation fee. No-shows forfeit the full rental amount.',
                },
                {
                    question: 'How long does a refund take?',
                    answer: 'Approved refunds are processed within 5-10 business days. The time for funds to appear in your account depends on your bank or card issuer.',
                },
                {
                    question: 'Can I get a refund for unused rental days?',
                    answer: 'If you return equipment early, the remaining days are not refundable. However, please contact us as we may be able to apply credit to a future rental.',
                },
                {
                    question: 'What happens if I need to extend my rental?',
                    answer: 'Contact us at least 24 hours before your return date to request an extension. Extensions are subject to availability and will be charged at the current daily rate.',
                },
            ],
        },
    ],
};
